<script setup lang="ts">
import { ref } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { groupsApi, type Group } from '@/api/groups';

interface Props {
  group: Group;
}

interface Emits {
  (e: 'close'): void;
  (e: 'success'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const queryClient = useQueryClient();
const submitButtonDisabled = ref(false);

const discardMutation = useMutation({
  mutationFn: () => groupsApi.destroy(props.group.id),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['groups'] });
    emit('success');
    emit('close');
  },
  onError: () => {
    submitButtonDisabled.value = false;
  },
});

function handleSubmit() {
  submitButtonDisabled.value = true;
  discardMutation.mutate();
}
</script>

<template>
  <v-card class="pa-4">
    <v-card-title>
      Archivar Grupo "{{ group.name }}"
    </v-card-title>
    <v-card-text>
      <p class="mb-4">
        ¿Está seguro que desea archivar el grupo "{{ group.name }}"?
      </p>
      <p class="mb-4 text-body-2 text-medium-emphasis">
        Los pacientes y profesionales actualmente asignados quedarán sin grupo.
        Los usuarios asignados al grupo perderán visibilidad sobre ellos.
      </p>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        :disabled="discardMutation.isPending.value"
        @click="emit('close')"
      >
        Cancelar
      </v-btn>
      <v-btn
        color="warning"
        :loading="discardMutation.isPending.value"
        :disabled="submitButtonDisabled"
        @click="handleSubmit"
      >
        Archivar
      </v-btn>
    </v-card-actions>

    <v-alert
      v-if="discardMutation.isError.value"
      type="error"
      variant="tonal"
      class="mt-4"
    >
      Ha ocurrido un error. Intenta nuevamente.
    </v-alert>
  </v-card>
</template>
